import {
  ModuleRegistry,
  TextFilterModule,
  NumberFilterModule,
  SelectEditorModule,
  enableDevValidations,
  LargeTextEditorModule,
  TextEditorModule,
  RowSelectionModule,
  CellStyleModule,
  RenderApiModule,
  PaginationModule,
  ClientSideRowModelApiModule,
  RowApiModule,
  TooltipModule,
  NumberEditorModule,
  RowStyleModule,
  HighlightChangesModule,
  themeAlpine,
  UndoRedoEditModule,
  PinnedRowModule,
  RowAutoHeightModule,
  ColumnApiModule,
  ScrollApiModule,
  CustomEditorModule
} from 'ag-grid-community';

import {
  ColumnsToolPanelModule,
  ClipboardModule,
  ContextMenuModule,
  ExcelExportModule,
  CsvExportModule,
  CellSelectionModule,
  LicenseManager,
  RichSelectModule,
  MasterDetailModule,
  ServerSideRowModelModule,
  ServerSideRowModelApiModule,
  RowGroupingModule,
  StatusBarModule,
  TreeDataModule,
  FindModule,
} from 'ag-grid-enterprise';

ModuleRegistry.registerModules([
  RowSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  SelectEditorModule,
  CellStyleModule,
  TextEditorModule,
  LargeTextEditorModule,
  RenderApiModule,
  PaginationModule,
  ClientSideRowModelApiModule,
  RowApiModule,
  TooltipModule,
  NumberEditorModule,
  RowStyleModule,
  HighlightChangesModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  ExcelExportModule,
  CsvExportModule,
  ClipboardModule,
  CellSelectionModule,
  RichSelectModule,
  UndoRedoEditModule,
  MasterDetailModule,
  ServerSideRowModelModule,
  ServerSideRowModelApiModule,
  PinnedRowModule,
  RowGroupingModule,
  StatusBarModule,
  RowAutoHeightModule,
  ColumnApiModule,
  TreeDataModule,
  FindModule,
  ScrollApiModule,
  CustomEditorModule,
]);
LicenseManager.setLicenseKey(process.env.AG_GRID_LICENSE_KEY);
import PaginationWrapper from '../../reusable/Paginator/PaginationWrapper.js';
import CustomTooltip from './CustomTooltip.js';
import { getMoneyObj } from '../../reusable/MoneyJsHelpers.js';

export const PAGINATION_PAGE_SIZE_SELECTOR_VALUES = [5, 10, 20, 50, 100];
export const DEFAULT_PAGINATION_PAGE_SIZE = 20;
export const TOOLTIP_SHOW_DELAY = 0;
export const undoRedoCellEditing = true;
export const undoRedoCellEditingLimit = 50;
export const findOptions = { caseSensitive: false, currentPageOnly: false };

if (process.env.NODE_ENV !== 'production') {
  enableDevValidations();
}

export const defaultColumnDef = {
  editable: true,
  floatingFilter: true,
  sortable: true,
  filter: true,
  resizable: true,
  enableCellChangeFlash: false,
  tooltipComponent: CustomTooltip,
  lockPinned: true,
  width: 300,
  cellDataType: false,
  valueParser: (params) => 
    typeof params.newValue === 'string' 
      ? params.newValue.trim() 
      : params.newValue
};

export const rowModelType = {
  clientSide: 'clientSide',
  serverSide: 'serverSide',
};

export const groupIcons = {
  groupExpanded: '<forge-icon external name="chevron_up"></forge-icon>',
  groupContracted: '<forge-icon external name="chevron_down"></forge-icon>',
};

export const AGGridColumnNumberComparator = (valueA, valueB) => {
  if (valueA == null && valueB == null) return 0;
  if (valueA == null) return -1;
  if (valueB == null) return 1;

  return Number(valueA) - Number(valueB);
};

export const AGGridTotalCostComparator = (a, b) => String(a).localeCompare(String(b), undefined, {
  numeric: true,
  sensitivity: 'base',
});

export const AGGridDateComparator = (date1, date2) => {
  const date1Number = dateToComparableNumber(date1);
  const date2Number = dateToComparableNumber(date2);

  if (date1Number === null && date2Number === null) return 0;
  if (date1Number === null) return -1;
  if (date2Number === null) return 1;

  return date1Number - date2Number;
};

const dateToComparableNumber = (date) => {
  if (date === undefined || date === null || date.length !== 8) return null;

  const yearNumber = Number.parseInt(date.substring(6, 8));
  const dayNumber = Number.parseInt(date.substring(3, 5));
  const monthNumber = Number.parseInt(date.substring(0, 2));

  return yearNumber * 10000 + monthNumber * 100 + dayNumber;
};

export const theme = themeAlpine
  .withParams(
    {
      selectedRowBackgroundColor: 'var(--forge-theme-primary-container-low)',
      rowHoverColor: '#00000014',
      checkboxCheckedBackgroundColor: 'var(--forge-theme-tertiary)',
      checkboxIndeterminateBackgroundColor: 'var(--forge-theme-tertiary)',
      oddRowBackgroundColor: 'var(--forge-theme-surface-dim)',
      sideButtonTextColor: 'var(--forge-theme-primary)',
      sideButtonHoverTextColor: 'var(--forge-theme-primary)',
      findMatchBackgroundColor: 'transparent',
      findMatchColor: '#FFFFFF',
      findActiveMatchBackgroundColor: 'transparent',
      findActiveMatchColor: '#FFFFFF',
      wrapperBorder: 'none',
    },
    'dark'
  )
  .withParams(
    {
      selectedRowBackgroundColor: 'var(--forge-theme-primary-container-low)',
      rowHoverColor: '#00000014',
      checkboxIndeterminateBackgroundColor: 'var(--forge-theme-tertiary)',
      checkboxCheckedBackgroundColor: 'var(--forge-theme-tertiary)',
      oddRowBackgroundColor: 'var(--forge-theme-surface-dim)',
      sideButtonTextColor: 'var(--forge-theme-primary)',
      sideButtonHoverTextColor: 'var(--forge-theme-primary)',
      findMatchBackgroundColor: 'transparent',
      findActiveMatchBackgroundColor: 'transparent',
      wrapperBorder: 'none'
    },
    'light'
  );

export const getRowId = params => params.data.id.toString();

export const getRowSelectionMode = () => {
  return {
    mode: 'multiRow',
    selectAll: 'currentPage',
    isRowSelectable: (params) => params.data && !params.data.Error
  };
};

export const rowClassRules = (props) => {  //might be performance heavy keep in mind
  return {
    'ag-row-error': function (params) {
      return params.data.Error || props.some(prop => params.data[prop]);
    },
  };
};

export const sideBar = {
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true,
        suppressColumnExpandAll: true,
        suppressColumnMove: true,
      },
    },
  ],
};

export const cellSelection = () => {
  return {
    handle: {
      mode: 'fill',
    },
  };
};

export const selectionColumnDef = () => {
  return {
    pinned: 'left',
  };
};

export const paginationPanel = (gridRef, totalCount, setPageSize, pageSize, onPageChange) => {
  return {
    statusPanels: [
      {
        statusPanel: PaginationWrapper,
        statusPanelParams: {
          pageSize: pageSize,
          pageSizeOptions: PAGINATION_PAGE_SIZE_SELECTOR_VALUES,
          totalCount: totalCount,
          currentPage: gridRef.current?.api?.paginationGetCurrentPage() || 0,
          handleChange: (e) => {
            if (e.detail.type === 'page-size') {
              setPageSize(e.detail.pageSize);
              gridRef.current.api.paginationGoToFirstPage();
              gridRef.current.api.ensureIndexVisible(0); //This is added due to Firefox issue with scroll not refreshing 
            } else {
              gridRef.current.api.paginationGoToPage(e.detail.pageIndex);
              onPageChange && onPageChange(e.detail.pageIndex);
            }
          },
        },
      },
    ],
  };
};

export const backendPaginationPanel = (totalCount, pageSize, setPageSize, currentPage, handlePaginationChange) => {
  return {
    statusPanels: [
      {
        statusPanel: PaginationWrapper,
        statusPanelParams: {
          pageSize: pageSize,
          pageSizeOptions: PAGINATION_PAGE_SIZE_SELECTOR_VALUES,
          totalCount: totalCount,
          currentPage: currentPage,
          handleChange: (e) => {
            if (e.detail.type === 'page-size') {
              setPageSize(e.detail.pageSize);
            } else {
              handlePaginationChange(e.detail.pageIndex);
            }
          }
        },
      },
    ],
  };
};

export const flashRow = (gridApi, flashId, setFlashId) => {
  let rowNode = gridApi.getRowNode(flashId.toString());
  gridApi.flashCells({ rowNodes: [rowNode] });
  setTimeout(() => {
    setFlashId(null);
  }, 500);
};

const RegisterAgGridModules = () => {
  return null;
};

export const getRowClass = () => {
  return (params) => {
    if (params.node.footer) {
      return 'bold';
    }
  };
};

export const customAggregateFunction = (params) => {
  const aggregatedValue = params.values.reduce(
    (sum, val) => sum.add(getMoneyObj(Number(val) || 0)),
    getMoneyObj(0),
  );

  return aggregatedValue;
};

export default RegisterAgGridModules;
